<template>
  <MetricsSummaryWithLoader
    :loading="loading"
    :number-of-summary-skeletons="4"
    totals-label-width="208px"
    :summaries="summaries"
    :project-name="projectName"
    :date-range="dateRange"
  />
</template>

<script setup>
import MetricsSummaryWithLoader from '@/components/insights/Metrics/MetricsSummaryWithLoader.vue';
import { computed } from 'vue';
import { isEmpty } from 'lodash-es';
import { formatTooltipValue } from '@/helpers/formatters';
import { useColors } from '@/composables/colors';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Array,
    default: () => ([]),
  },
  tooltips: {
    type: Object,
    default: () => ({}),
  },
  projectName: {
    type: String,
    default: '',
  },
  dateRange: {
    type: Object,
    default: () => ({}),
  },
});

const { brandPrimary } = useColors();

const summaries = computed(() => {
  if (isEmpty(props.data)) return [];

  const prospecting = (props.data || []).find((item) => item.category === 'prospecting') || {};
  const agency = (props.data || []).find((item) => item.category === 'agencies') || {};

  const totalProspectingApplications = prospecting.review || 0;
  const totalAgencyApplications = agency.review || 0;
  const totalProspectingHires = prospecting.hired || 0;
  const totalAgencyHires = agency.hired || 0;
  const totalApplications = totalProspectingApplications + totalAgencyApplications;
  const totalHires = totalProspectingHires + totalAgencyHires;

  const baseSummaries = [
    {
      id: 'prospecting_applications',
      tooltipKey: 'outbound-apps-from-prospecting-sources',
      label: 'Applications from Prospecting Sources',
      color: brandPrimary.value,
      value:
        typeof prospecting?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((prospecting.review / totalApplications) * 100, 'percent')
          : '-',
    },
    {
      id: 'prospecting_hired',
      tooltipKey: 'outbound-hires-from-prospecting-sources',
      label: 'Hires from Prospecting Sources',
      color: brandPrimary.value,
      value:
        typeof prospecting?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((prospecting.hired / totalHires) * 100, 'percent')
          : '-',
    },
    {
      id: 'agencies_applications',
      tooltipKey: 'outbound-apps-from-agency-sources',
      label: 'Applications from Agency Sources',
      color: brandPrimary.value,
      value:
        typeof agency?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((agency.review / totalApplications) * 100, 'percent')
          : '-',
    },
    {
      id: 'agencies_hired',
      tooltipKey: 'outbound-hires-from-agency-sources',
      label: 'Hires from Agency Sources',
      color: brandPrimary.value,
      value:
        typeof agency?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((agency.hired / totalHires) * 100, 'percent')
          : '-',
    },
  ];

  return baseSummaries.map((summary) => {
    const { body_r: html, mode } = props.tooltips?.[summary.tooltipKey] || {};
    return {
      ...summary,
      tooltip: { html, mode, key: summary.tooltipKey },
    };
  });
});
</script>
